import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  SxProps,
  Theme,
} from '@mui/material';
import { SvgIconProps } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { CircleIconBadge, Type } from './circle_icon_badge';

export enum FeedbackType {
  Primary,
  Error,
  Warning,
}

/**
 * FeedbackPopup's view model
 *
 * @param {string} primaryButtonText: The main button's text
 * @param {string} secondaryButtonText: The secondary button's text
 * @param {string} title: The feedback's title
 * @param {string} subtitle: The feedback's subtitle
 * @param {React.ComponentType<SvgIconProps>} icon: The icon component to display in the feedback popup
 * @param {FeedbackType} type: The type of the feedback: Primary | Error | Warning
 */
export type FeedbackPopupViewModel = {
  primaryButtonText: string;
  secondaryButtonText?: string;
  title: string;
  subtitle?: string;
  icon: React.ComponentType<SvgIconProps>;
  type: FeedbackType;
};

type FeedbackPopupProps = {
  data: FeedbackPopupViewModel;
  isDialogOpen: boolean;
  onPrimaryButtonClick: () => void;
  onSecondaryButtonClick?: () => void;
  handleDialogClose: () => void;
  children?: React.ReactNode;
  width?: string;
  minHeight?: string;
  validationError?: string;
  primaryButtonTestId?: string;
  secondaryButtonTestId?: string;
  dialogTestId?: string;
  dialogTitleSx?: SxProps<Theme>;
};

const IconHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'feedbacktype',
})<{ feedbacktype: FeedbackType }>(({ feedbacktype, theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '24px',
  '& .feedback-popup--header--icon': {
    boxShadow: `0px 16px 26px 0px ${
      feedbacktype === FeedbackType.Error
        ? theme.palette.error.main + '5C'
        : feedbacktype === FeedbackType.Warning
          ? theme.palette.warning.main + '5C'
          : theme.palette.primary.main + '5C'
    }`,
  },
}));

const Subtitle = styled(Box)(({ theme }) => ({
  marginTop: '6px',
  fontWeight: 500,
  fontSize: theme.typography.subtitle1.fontSize,
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const ValidationError = styled(Box)(({ theme }) => ({
  marginBottom: '-24px',
  minHeight: '24px',
  textAlign: 'center',
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.error.main,
  fontWeight: 500,
}));

/**
 * FeedbackPopup component for displaying modal dialogs with customizable content and actions.
 *
 * @param data - The feedback popup view model containing content and styling
 * @param isDialogOpen - Whether the dialog is open
 * @param onPrimaryButtonClick - Callback for primary button click
 * @param onSecondaryButtonClick - Optional callback for secondary button click
 * @param handleDialogClose - Callback for dialog close
 * @param children - Optional children to render in addition to subtitle
 * @param width - Optional width for the dialog (defaults to '420px')
 * @param minHeight - Optional minimum height for the dialog (defaults to '340px')
 * @param validationError - Optional validation error message to display under footer
 * @param dialogTitleSx - Optional sx overrides for the dialog title
 */
export const FeedbackPopup: React.FC<FeedbackPopupProps> = ({
  data,
  isDialogOpen,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  handleDialogClose,
  children,
  width,
  minHeight,
  validationError,
  primaryButtonTestId,
  secondaryButtonTestId,
  dialogTestId,
  dialogTitleSx,
}) => {
  const theme = useTheme();
  // Map FeedbackType to CircleIconBadge Type
  const getIconBadgeType = (feedbackType: FeedbackType): Type => {
    switch (feedbackType) {
      case FeedbackType.Error:
        return Type.Error;
      case FeedbackType.Warning:
        return Type.Warning;
      case FeedbackType.Primary:
      default:
        return Type.Info;
    }
  };

  const iconBadgeType = getIconBadgeType(data.type);

  const getButtonColor = (
    feedbackType: FeedbackType,
  ): 'primary' | 'error' | 'info' => {
    switch (feedbackType) {
      case FeedbackType.Error:
        return 'error';
      case FeedbackType.Warning:
        return 'info'; // MUI doesn't have warning color for buttons, use info
      case FeedbackType.Primary:
      default:
        return 'primary';
    }
  };

  const buttonColor = getButtonColor(data.type);

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleDialogClose}
      data-testid={dialogTestId}
      slotProps={{
        paper: {
          sx: {
            paddingY: '30px',
            width: width || '380px',
            minHeight: minHeight || '320px',
          },
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: 'center', padding: '0 24px', ...dialogTitleSx }}
      >
        <IconHeader feedbacktype={data.type}>
          <CircleIconBadge
            className='feedback-popup--header--icon'
            data={{
              icon: data.icon,
              type: iconBadgeType,
            }}
          />
        </IconHeader>
        {data.title}
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', paddingBottom: 0 }}>
        {data.subtitle && <Subtitle>{data.subtitle}</Subtitle>}
        {children}
      </DialogContent>

      <DialogActions
        sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 0 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          {data.secondaryButtonText && onSecondaryButtonClick && (
            <Button
              onClick={onSecondaryButtonClick}
              variant='text'
              size='large'
              sx={{
                color: theme.palette.text.secondary,
              }}
              data-testid={secondaryButtonTestId}
            >
              {data.secondaryButtonText.toUpperCase()}
            </Button>
          )}
          <Button
            onClick={onPrimaryButtonClick}
            variant='contained'
            color={buttonColor}
            size='large'
            disabled={!!validationError}
            data-testid={primaryButtonTestId}
          >
            {data.primaryButtonText.toUpperCase()}
          </Button>
        </Box>
        <ValidationError>{validationError}</ValidationError>
      </DialogActions>
    </Dialog>
  );
};
